<script setup lang="ts">
import { ref, watch } from 'vue';
import Draggable from 'vuedraggable';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import Item from '@/components/menu/common/item.vue';

type ItemProps = InstanceType<typeof Item>['$props'];

const props = withDefaults(
  defineProps<{
    title?: string;
    items: ItemProps[];
    isFoldable?: boolean;
    initialIsOpen?: boolean;
    isSortable?: boolean;
  }>(),
  {
    isFoldable: false,
    initialIsOpen: true,
    isSortable: false,
  },
);

const emit = defineEmits<{
  'menu-list-is-open': [isOpen: boolean];
  'menu-list-order-changed': [
    payload: { from: number; to: number; items: ItemProps[] },
  ];
}>();

const itemsInternal = ref([...props.items]);
watch(
  () => props.items,
  () => (itemsInternal.value = [...props.items]),
);

const isOpen = ref(props.initialIsOpen);
const toggleIsOpen = () => {
  if (!props.isFoldable) return;

  isOpen.value = !isOpen.value;
  emit('menu-list-is-open', isOpen.value);
};

const isDragging = ref(false);
const start = () => {
  if (props.isSortable) isDragging.value = true;
};
const move = () => isDragging.value;
const update = async (item: { oldIndex: number; newIndex: number }) => {
  isDragging.value = false;
  if (item.oldIndex === item.newIndex) {
    return;
  }

  emit('menu-list-order-changed', {
    from: item.oldIndex,
    to: item.newIndex,
    items: [...itemsInternal.value],
  });
};
</script>

<template>
  <div class="item-list" :class="{ foldable: isFoldable }">
    <div v-if="title" class="title-icon c-title-bold--s" @click="toggleIsOpen">
      <span class="title">
        {{ title }}
      </span>
      <DgrIcon
        size="small"
        :name="isOpen ? 'chevron-up' : 'chevron-down'"
        class="toggle-icon small"
        v-if="isFoldable"
      />
    </div>
    <div v-if="isOpen" class="items">
      <Draggable
        :disabled="!isSortable"
        :list="itemsInternal"
        item-key="id"
        tag="ul"
        ghost-class="ghost"
        :move="move"
        @start="start"
        @end="update"
      >
        <template #item="{ element: item }">
          <li class="item">
            <Item v-bind="{ ...item, isDraggable: isSortable }" />
          </li>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style scoped lang="scss">
.item-list {
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  // 旧レイアウトは項目の幅がメニュー全体の幅を決めるので240px固定。
  // 新レイアウトはパネル幅が可変なので、パネル側から100%を渡す
  width: var(--menu-item-width, 240px);

  .title-icon {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2px;

    box-sizing: border-box;
    height: 24px;
    padding: 4px;

    .title {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  &.foldable {
    .title-icon {
      cursor: pointer;
      height: 24px;
      padding: 8px;
    }

    .toggle-icon {
      flex-shrink: 0;
    }
  }

  .items ul {
    list-style: none;
    padding: 0;
    .ghost {
      visibility: hidden;
    }
  }
}
</style>
